import { TaskStatus } from '@models/task.model';
import COLORS from '@utils/color';

export const TaskOptions = [
  { id: 'Demolition', name: 'Demolition' },
  { id: 'Drywall', name: 'Drywall' },
  { id: 'Electrical', name: 'Electrical' },
  { id: 'Foundation', name: 'Foundation' },
  { id: 'Framing', name: 'Framing' },
  { id: 'HVAC', name: 'HVAC' },
  { id: 'Insulation', name: 'Insulation' },
  { id: 'Landscaping', name: 'Landscaping' },
  { id: 'Mechanical', name: 'Mechanical' },
  { id: 'Painting', name: 'Painting' },
  { id: 'Plumbing', name: 'Plumbing' },
  { id: 'Roofing', name: 'Roofing' },
  { id: 'Other', name: 'Other' },
];

export const TimeOptions = [
  { id: 'hours', name: 'hours' },
  { id: 'days', name: 'days' },
  { id: 'weeks', name: 'weeks' },
];

// Story-071 (sprint 14) pins these `name` values byte-identical to English in
// `options.test.ts` (a label-stability regression guard, not an i18n contract) — do not repoint
// `name` at translated text. Sprint 28, story-144 review Critical #1 fix: rendering call sites
// must use `getTranslatedTaskStatusLabel`/`getTranslatedTaskStatusOptions` below instead of this
// array's own `.name`, which stays English-only for that pre-existing test.
export const TaskStatusOptions = [
  { id: TaskStatus.ACTIVE, name: 'Active', color: COLORS.green },
  { id: TaskStatus.INACTIVE, name: 'Inactive', color: COLORS.orange },
  { id: TaskStatus.COMPLETE, name: 'Complete', color: COLORS.lightBlue },
  {
    id: TaskStatus.CLIENT_APPROVAL,
    name: 'Pending Client Approval',
    color: COLORS.darkGreen,
  },
  {
    id: TaskStatus.INTERNAL_APPROVAL,
    name: 'Pending Internal Approval',
    // Story-071 (sprint 14): was COLORS.darkGreen (#117864, same as Client Approval),
    // contradicting the canonical palette in color.ts / taskDisplay.ts, where Internal
    // Approval is COLORS.darkerGreen (#0e5a47).
    color: COLORS.darkerGreen,
  },
  { id: TaskStatus.REJECTED, name: 'Rejected', color: COLORS.error },
  {
    id: TaskStatus.CANCELED,
    name: 'Canceled',
    // Story-071 (sprint 14): was COLORS.error (#b30000, same as Rejected), contradicting the
    // canonical palette, where Canceled is COLORS.cancel (#FA8072).
    color: COLORS.cancel,
  },
  {
    id: TaskStatus.DELEGATION_CANCELED,
    name: 'Delegation Canceled',
    color: COLORS.violet,
  },
];

/**
 * Sprint 28, story-144 review Critical #1 fix. `options.ts` is a non-component module, so per
 * docs/architecture.md "## Sprint 28" Decision 2 it cannot call `useTranslation` itself — every
 * caller passes its own `useTranslation('taskDetail')` result in. Mirrors mobile's
 * `TaskScreen.translateTaskStatus` and reads the same `taskDetail.status*` keys (exact match,
 * house style §5's canonical Spanish), so the dropdown, the status-change confirmation sentence,
 * and the subtask status column all render the same translated text mobile already does.
 */
export const getTranslatedTaskStatusLabel = (
  status: string,
  tTaskDetail: { [key: string]: any },
): string => {
  switch (status) {
    case TaskStatus.ACTIVE:
      return tTaskDetail.statusActive;
    case TaskStatus.INACTIVE:
      return tTaskDetail.statusInactive;
    case TaskStatus.COMPLETE:
      return tTaskDetail.statusComplete;
    case TaskStatus.CLIENT_APPROVAL:
      return tTaskDetail.statusClientApproval;
    case TaskStatus.INTERNAL_APPROVAL:
      return tTaskDetail.statusInternalApproval;
    case TaskStatus.REJECTED:
      return tTaskDetail.statusRejected;
    case TaskStatus.CANCELED:
      return tTaskDetail.statusCanceled;
    case TaskStatus.DELEGATION_CANCELED:
      return tTaskDetail.statusDelegationCanceled;
    default:
      return status;
  }
};

/** Same shape as `TaskStatusOptions`, with `name` translated via `getTranslatedTaskStatusLabel`. */
export const getTranslatedTaskStatusOptions = (tTaskDetail: { [key: string]: any }) =>
  TaskStatusOptions.map((option) => ({
    ...option,
    name: getTranslatedTaskStatusLabel(option.id, tTaskDetail),
  }));

export const PeriodOptions = [
  { id: 'Daily', name: 'Daily' },
  { id: 'Weekly', name: 'Weekly' },
  { id: 'Monthly', name: 'Monthly' },
];

export const FullTimeOptions = [
  { id: 'FullTime', name: 'FullTime' },
  { id: 'PartTime', name: 'PartTime' },
];

export const RolesOptions = [
  { id: 'ADMIN', name: 'ADMIN' },
  { id: 'PROJECT_MANAGER', name: 'PROJECT MANAGER' },
  { id: 'SUPERINTENDENT', name: 'SUPERINTENDENT' },
  { id: 'CREW_LEAD', name: 'CREW LEAD' },
  { id: 'EMPLOYEE', name: 'EMPLOYEE' },
];

export const AllRoleOptions = [
  { id: 'ADMIN', name: 'ADMIN' },
  { id: 'PROJECT_MANAGER', name: 'PROJECT MANAGER' },
  { id: 'SUPERINTENDENT', name: 'SUPERINTENDENT' },
  { id: 'CREW_LEAD', name: 'CREW LEAD' },
  { id: 'EMPLOYEE', name: 'EMPLOYEE' },
];

export const WeightOptions = [
  { id: 'metric', name: 'Metric', group: 'System' },
  { id: 'imperial', name: 'Imperial', group: 'System' },
];

export const MetricWeightOptions = [
  // Length
  { id: 'millimeters', name: 'mm (millimeters)', group: 'Length' },
  { id: 'centimeters', name: 'cm (centimeters)', group: 'Length' },
  { id: 'meters', name: 'm (meters)', group: 'Length' },
  // Area
  { id: 'squareMillimeters', name: 'mm² (square millimeters)', group: 'Area' },
  { id: 'squareCentimeters', name: 'cm² (square centimeters)', group: 'Area' },
  { id: 'squareMeters', name: 'm² (square meters)', group: 'Area' },
  // Volume
  { id: 'cubicMillimeters', name: 'mm³ (cubic millimeters)', group: 'Volume' },
  { id: 'cubicCentimeters', name: 'cm³ (cubic centimeters)', group: 'Volume' },
  { id: 'cubicMeters', name: 'm³ (cubic meters)', group: 'Volume' },
  { id: 'milliliters', name: 'mL (milliliter)', group: 'Volume' },
  { id: 'liters', name: 'L (liter)', group: 'Volume' },
  // Weight
  { id: 'milligrams', name: 'mg (milligram)', group: 'Weight' },
  { id: 'grams', name: 'g (gram)', group: 'Weight' },
  { id: 'kilograms', name: 'kg (kilogram)', group: 'Weight' },
  { id: 'tonnes', name: 't (tonne)', group: 'Weight' },
  // Miscellaneous
  { id: 'units', name: 'units', group: 'Miscellaneous' },
  { id: 'boxes', name: 'boxes', group: 'Miscellaneous' },
  { id: 'scoops', name: 'scoops', group: 'Miscellaneous' },
];

export const ImperialWeightOptions = [
  // Length
  { id: 'inches', name: 'in (inches)', group: 'Length' },
  { id: 'feet', name: 'ft (feet)', group: 'Length' },
  { id: 'yards', name: 'yd (yards)', group: 'Length' },
  { id: 'boardFeet', name: 'board ft', group: 'Length' },
  { id: 'linearFeet', name: 'linear ft', group: 'Length' },
  // Area
  { id: 'squareInches', name: 'in² (square inches)', group: 'Area' },
  { id: 'squareFeet', name: 'ft² (square feet)', group: 'Area' },
  { id: 'squareYards', name: 'yd² (square yards)', group: 'Area' },
  // Volume
  { id: 'cubicInches', name: 'in³ (cubic inches)', group: 'Volume' },
  { id: 'cubicFeet', name: 'ft³ (cubic feet)', group: 'Volume' },
  { id: 'cubicYards', name: 'yd³ (cubic yards)', group: 'Volume' },
  { id: 'fluidOunces', name: 'fl oz (fluid ounce)', group: 'Volume' },
  { id: 'cups', name: 'cup', group: 'Volume' },
  { id: 'pints', name: 'pt (pint)', group: 'Volume' },
  { id: 'quarts', name: 'qt (quart)', group: 'Volume' },
  { id: 'gallons', name: 'gal (gallon)', group: 'Volume' },
  // Weight
  { id: 'ounces', name: 'oz (ounce)', group: 'Weight' },
  { id: 'pounds', name: 'lb (pound)', group: 'Weight' },
  { id: 'tons', name: 'tn (ton)', group: 'Weight' },
  // Miscellaneous
  { id: 'units', name: 'units', group: 'Miscellaneous' },
  { id: 'boxes', name: 'boxes', group: 'Miscellaneous' },
  { id: 'scoops', name: 'scoops', group: 'Miscellaneous' },
];

export const EventTypeOptions = [
  { id: 'Incident', name: 'Incident' },
  { id: 'Visitor', name: 'Visitor' },
  { id: 'Permit', name: 'Permit' },
  { id: 'Communication', name: 'Communication' },
  { id: 'Inspection', name: 'Inspection' },
  { id: 'Other', name: 'Other' },
];

export const IncidentTypeOptions = [
  { id: 'Weather', name: 'Weather' },
  { id: 'Accident', name: 'Accident' },
  { id: 'Materials issue', name: 'Materials issue' },
  { id: 'Equipment issue', name: 'Equipment issue' },
  { id: 'Add incident type', name: 'Add incident type' },
];

export const InspectionTypeOptions = [
  { id: 'Foundation', name: 'Foundation' },
  { id: 'Electric', name: 'Electric' },
  { id: 'HVAC', name: 'HVAC' },
  { id: 'Mechanical', name: 'Mechanical' },
  { id: 'Roofing', name: 'Roofing' },
  { id: 'Interior', name: 'Interior' },
  { id: 'Saftey', name: 'Saftey' },
  { id: 'Final', name: 'Final' },
  { id: 'Exterior', name: 'Exterior' },
  { id: 'Appliance', name: 'Appliance' },
  { id: 'Fixtures', name: 'Fixtures' },
  { id: 'Wooden supports', name: 'Wooden supports' },
  { id: 'Framing', name: 'Framing' },
  { id: 'Other', name: 'Other' },
];
